import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Pencil, Trash2, User, UserPlus, PlusCircle, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { LABELS, toEnum } from '../utils/clinicalSetting';

// --- NEW HELPER FUNCTION ---
// This function determines the correct navigation path based on the patient's clinical setting.
const getPatientDetailPath = (patient) => {
  const setting = patient.clinicalSetting; // Uses the canonical 'clinicalSetting' field
  
  switch (setting) {
    case 'emergencyRoom':
      return `/ed-detail/${patient.id}`;
    case 'inpatient':
      return `/inpatient-detail/${patient.id}`; // Assuming this route exists
    case 'outpatient':
      return `/outpatient-detail/${patient.id}`; // Assuming this route exists
    case 'consult':
      return `/consult-detail/${patient.id}`; // Assuming this route exists
    default:
      // Fallback for any other cases or older data models
      return `/patient/${patient.id}/detail`;
  }
};

const PatientCard = ({ patient, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const genderIcon = (sex) => {
    return sex?.toLowerCase() === 'female'
      ? <UserPlus className="text-pink-400" size={22} />
      : <User className="text-sky-500" size={22} />;
  };

  // --- UPDATED NAVIGATION LOGIC ---
  const handleNavigate = () => {
    const path = getPatientDetailPath(patient);
    navigate(path);
  };

  return (
    <div
      onClick={handleNavigate} // Use the new handler
      className="group cursor-pointer bg-slate-50 dark:bg-zinc-800/50 rounded-2xl p-5 shadow-sm hover:shadow-lg hover:scale-[1.02] transition-all duration-300 ease-in-out"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="bg-white dark:bg-zinc-700 p-3 rounded-full">{genderIcon(patient.sex)}</div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-white">{patient.firstName} {patient.lastName || ''}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Room: {patient.roomNumber || 'N/A'} • Age: {patient.age || 'N/A'}</p>
          </div>
        </div>
        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500"><Pencil size={16} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500"><Trash2 size={16} /></button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-zinc-700">
        <p className="text-sm text-gray-600 dark:text-gray-300 truncate"><span className="font-medium">CC:</span> {patient.chiefComplaint || 'No chief complaint'}</p>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [activeSetting, setActiveSetting] = useState(() =>
    localStorage.getItem('clinicalSettingLabel') || 'Inpatient'
  );

  useEffect(() => {
    localStorage.setItem('clinicalSettingLabel', activeSetting);
    localStorage.setItem('clinicalSetting', toEnum(activeSetting));
  }, [activeSetting]);

  const fetchPatients = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);

    const ownerQ = where('ownerId', '==', currentUser.uid);
    const order = orderBy('dateAdded', 'desc');
    let data = [];

    if (activeSetting === 'All') {
      const qAll = query(collection(db, 'patients'), ownerQ, order);
      const snap = await getDocs(qAll);
      data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } else {
      const enumVal = toEnum(activeSetting);
      const q = query(collection(db, 'patients'), ownerQ, where('clinicalSetting', '==', enumVal), order);
      const snap = await getDocs(q);
      data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    setPatients(data);
    setIsLoading(false);
  }, [currentUser, activeSetting]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'patients', id));
    setConfirmDeleteId(null);
    fetchPatients();
  };

  const settingsTabs = LABELS;

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto py-10 px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">Dashboard</h1>
          <button
            onClick={() => navigate('/patients/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all duration-300"
          >
            <PlusCircle size={18} /> New Patient
          </button>
        </div>

        {/* Center tabs */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
            {settingsTabs.map(setting => (
              <button
                key={setting}
                onClick={() => setActiveSetting(setting)}
                className={`relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300 ${
                  activeSetting === setting ? 'text-gray-800 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                }`}
              >
                <span className="relative z-10">{setting}</span>
                {activeSetting === setting && (
                  <motion.div
                    layoutId="active-pill"
                    className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Patient list */}
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6 min-h-[400px]">
          {isLoading ? (
            <div className="text-center p-10 text-gray-500">Loading...</div>
          ) : patients.length === 0 ? (
            <div className="text-center p-10">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">No Patients Found</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2">Add a new patient to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {patients.map(patient => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  onEdit={() => navigate(`/edit-patient/${patient.id}`)}
                  onDelete={() => setConfirmDeleteId(patient.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Confirm delete */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-zinc-800 p-8 rounded-2xl shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">Delete Patient</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Are you sure? This action cannot be undone.</p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="py-2.5 bg-slate-200 dark:bg-zinc-700 text-gray-800 dark:text-white rounded-lg font-semibold hover:bg-slate-300 dark:hover:bg-zinc-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDeleteId)}
                className="py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}